import React, { useState, useCallback, useMemo, useContext } from "react";
import moment from "moment";
import {
  BackLink,
  Title,
  Text,
  Button,
  Autocomplete,
  Field,
  DateTimePickerV2,
  InnerLabelTextfield,
} from "components/commons";
import { ConfirmModal, SuccessModal } from "components/modals";
import { useModal, useRouter, useApi } from "hooks";
import locale from "localization";
import { Path } from "enums";
import UserContext from "contexts/user.context";
import { createMovement } from "apis";
import { prettifyProductCode, prettifyProductCodeStyle } from "utils/pretty.utils";
import styles from "./price-movement-add.module.scss";

const PRODUCTS = [
  { code: "gas105" },
  { code: "gas97" },
  { code: "gas95" },
  { code: "gas91" },
  { code: "diesel" },
  { code: "kerosene" },
  { code: "lpg" },
];

const MOVEMENT_TYPE_OPTIONS = [
  { label: "Rollback", value: "ROLLBACK" },
  { label: "Increase", value: "INCREASE" },
  { label: "No Movement", value: "NO_MOVEMENT" },
];

const AMOUNT_MAX = 999.99;

const isNumericPeriod = (val) => {
  if (val === "" || val === undefined) return true;
  return /^[0-9]*\.?[0-9]{0,2}$/.test(val);
};

const fmt2 = (val) => (parseFloat(val) || 0).toFixed(2);

const createInitialRow = (product) => ({
  productCode: product.code,
  movementType: "NO_MOVEMENT",
  amount: "0.00",
});

const ProductDot = ({ code }) => (
  <div
    style={{
      ...prettifyProductCodeStyle(code),
      borderRadius: "50%",
      width: "12px",
      height: "12px",
    }}
  />
);

const PriceMovementAddModule = () => {
  const { history } = useRouter();
  const { user } = useContext(UserContext);
  const confirmModal = useModal();
  const successModal = useModal();

  const [effectiveDateTime, setEffectiveDateTime] = useState(null);

  const [productRows, setProductRows] = useState(PRODUCTS.map(createInitialRow));
  const [isSaving, setIsSaving] = useState(false);

  const { request: submitCreate } = useApi({
    api: createMovement,
    handleOwnError: true,
  });

  const processedBy = user?.fullName || user?.name || user?.email || "";

  const isGasoline = (code) => code === "gas95" || code === "gas91";

  const handleMovementTypeChange = useCallback(
    (index, value) => {
      setProductRows((prev) =>
        prev.map((row, i) => {
          const shouldSync = i === index || (prev[index].productCode === "gas97" && isGasoline(row.productCode));
          if (!shouldSync) return row;
          return { ...row, movementType: value, amount: value === "NO_MOVEMENT" ? "0.00" : row.amount };
        })
      );
    },
    []
  );

  const handleAmountChange = useCallback(
    (index, value) => {
      setProductRows((prev) =>
        prev.map((row, i) => {
          const shouldSync = i === index || (prev[index].productCode === "gas97" && isGasoline(row.productCode));
          return shouldSync ? { ...row, amount: value } : row;
        })
      );
    },
    []
  );

  const handleAmountBlur = useCallback(
    (index, value) => {
      const clamped = fmt2(Math.min(Math.max(parseFloat(value) || 0, 0), AMOUNT_MAX));
      setProductRows((prev) =>
        prev.map((row, i) => {
          const shouldSync = i === index || (prev[index].productCode === "gas97" && isGasoline(row.productCode));
          return shouldSync ? { ...row, amount: clamped } : row;
        })
      );
    },
    []
  );

  const isFormValid = useMemo(() => {
    if (!effectiveDateTime) return false;
    return productRows.some(
      (row) => row.movementType === "ROLLBACK" || row.movementType === "INCREASE"
    );
  }, [effectiveDateTime, productRows]);

  const handleSaveClick = useCallback(() => {
    if (!isFormValid) return;
    confirmModal.show();
  }, [isFormValid, confirmModal]);

  const handleConfirmSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const effectiveAt = moment(effectiveDateTime).seconds(0).milliseconds(0).toISOString();
      const payload = {
        effectiveAt,
        createdBy: processedBy,
        createdByEmail: user?.email || "",
        // type: "nationwide",
        movementDetails: productRows.map((row) => {
          const isRollback = row.movementType === "ROLLBACK";
          return {
            productCode: row.productCode,
            // type: row.movementType,
            amount: parseFloat(isRollback ? -row.amount : row.amount) || 0,
          };
        }),
      };
      await submitCreate(payload);
      confirmModal.close();
      successModal.show();
    } catch {
      // error modal handled by useApi
    } finally {
      setIsSaving(false);
    }
  }, [effectiveDateTime, processedBy, user, productRows, submitCreate, confirmModal, successModal]);

  const handleSuccessGotIt = useCallback(() => {
    successModal.close();
    history.push(Path.PriceMovementList);
  }, [successModal, history]);

  const SummaryTable = () => (
    <table className={styles.summaryTable}>
      <thead>
        <tr>
          <th>{locale.products}</th>
          <th>{locale.priceMovement}</th>
          <th>{locale.amount}</th>
        </tr>
      </thead>
      <tbody>
        {productRows.map((row) => {
          const movementLabel =
            MOVEMENT_TYPE_OPTIONS.find((o) => o.value === row.movementType)?.label || "";
          const isIncrease = row.movementType === "INCREASE";
          const isRollback = row.movementType === "ROLLBACK";
          return (
            <tr key={row.productCode}>
              <td>
                <div className={styles.productCell}>
                  <div style={{ flexShrink: 5 }}>
                    <ProductDot code={row.productCode} />
                  </div>
                  <Text>{prettifyProductCode(row.productCode)}</Text>
                </div>
              </td>
              <td
                className={
                  isIncrease ? styles.increase : isRollback ? styles.rollback : styles.noMovement
                }
              >
                {movementLabel}
              </td>
              <td
                className={
                  isIncrease ? styles.increase : isRollback ? styles.rollback : styles.noMovement
                }
              >
                ₱{fmt2(row.amount)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const getMovementTypeColor = (movementType) => {
    const isIncrease = movementType === "INCREASE";
    const isRollback = movementType === "ROLLBACK";

    return isIncrease ? "#27ae60" : isRollback ? "#e74c3c" : "#95a2aa";
  };

  return (
    <div className={styles.container} data-testid="pm-add-page">
      <ConfirmModal
        {...confirmModal}
        title={locale.savePriceMovement}
        content={<Text style={{ color: "black" }}>{locale.savePriceMovementContent}</Text>}
        primary={{
          text: locale.savePriceMovementBtn,
          onClick: handleConfirmSave,
          loading: isSaving,
        }}
        secondary={{ text: locale.cancel }}
      >
        <SummaryTable />
      </ConfirmModal>
      <SuccessModal
        {...successModal}
        title={locale.success}
        content={locale.detailsHaveBeenSuccessfullyUpdated}
        primary={{ text: locale.gotIt, onClick: handleSuccessGotIt }}
      />

      <BackLink text={locale.priceMovement} path={Path.PriceMovementList} />

      <div className={styles.header}>
        <Title>{locale.addPriceMovement}</Title>
      </div>

      <div className={styles.formSection}>
        {/* ── Effective Date and Time ── */}
        <Field label="Effective Date and Time" labelPosition={"left"} className={styles.dateField}>
          <DateTimePickerV2
            name="effectiveDate"
            value={effectiveDateTime}
            placeholder="Select date"
            onChange={(name, { value }) => setEffectiveDateTime(value)}
            minDateTime={new Date()}
            data-testid="pm-add-effective-date"
          />
        </Field>

        {/* ── Read-only fields ── */}
        <div className={styles.fieldRow}>
          <Text className={styles.fieldLabel}>{locale.processedBy}</Text>
          <Text className={styles.fieldValue}>{processedBy}</Text>
        </div>
        <div className={styles.fieldRow}>
          <Text className={styles.fieldLabel}>{locale.priceMovementType}</Text>
          <Text className={styles.fieldValue}>Nationwide</Text>
        </div>
        <div className={styles.fieldRow}>
          <Text className={styles.fieldLabel}>{locale.priceMovementSubtype}</Text>
          <Text className={styles.fieldValue}>{locale.NA}</Text>
        </div>
      </div>

      {/* ── Products Table ── */}
      <div className={styles.tableWrapper}>
        <table className={styles.productsTable}>
          <thead>
            <tr>
              <th>{locale.products}</th>
              <th>{locale.priceMovement}</th>
              <th>{locale.amount}</th>
            </tr>
          </thead>
          <tbody>
            {productRows.map((row, i) => {
              const isNoMovement = row.movementType === "NO_MOVEMENT";
              const isFollower = isGasoline(row.productCode);
              return (
                <tr key={row.productCode} data-testid={`pm-add-row-${row.productCode}`}>
                  <td>
                    <div className={styles.productCell}>
                      <div style={{ flexShrink: 5 }}>
                        <ProductDot code={row.productCode} />
                      </div>
                      <Text style={{ color: "black" }}>{prettifyProductCode(row.productCode)}</Text>
                    </div>
                  </td>
                  <td>
                    <Autocomplete
                      className={styles.movementSelect}
                      value={row.movementType}
                      options={MOVEMENT_TYPE_OPTIONS}
                      placeholder="Select Price Movement"
                      disabled={isFollower}
                      disableClearable
                      onChange={(_, { value }) => handleMovementTypeChange(i, value)}
                      inputStyle={{
                        color: row.movementType
                          ? getMovementTypeColor(row.movementType)
                          : undefined,
                      }}
                      data-testid={`pm-add-movement-type-${row.productCode}`}
                    />
                  </td>
                  <td>
                    <div className={styles.amountCell}>
                      <InnerLabelTextfield
                        className={styles.amountInput}
                        value={row.amount}
                        placeholder="0.00"
                        disabled={isNoMovement || isFollower}
                        onChange={(_, { value }) => {
                          if (isNumericPeriod(value) && parseFloat(value || 0) <= AMOUNT_MAX)
                            handleAmountChange(i, value);
                        }}
                        onBlur={(_, { value }) => handleAmountBlur(i, value)}
                        prefix="₱"
                        fontColor={
                          row.movementType ? getMovementTypeColor(row.movementType) : undefined
                        }
                        data-testid={`pm-add-amount-${row.productCode}`}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className={styles.saveContainer}>
        <Button
          primary
          className={!isFormValid ? styles.saveBtnInactive : undefined}
          onClick={handleSaveClick}
          data-testid="pm-add-save-btn"
        >
          {locale.save}
        </Button>
      </div>
    </div>
  );
};

export default PriceMovementAddModule;
